import React from 'react';
import { Helmet } from 'react-helmet-async';
import SeoHead from '../../../shared/seo/SeoHead';
import { PomPomsSeo } from '../seo/PomPomsSeo';
import '../styles/pompoms-page.css';

const FAQ_ICON = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-2h2zm0-4h-2V7h2z" />
    </svg>
);

const TOGGLE_ICON = (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
    </svg>
);

const PRODUCT_TYPES = [
    {
        emoji: '🌺',
        name: 'Faux Fur Pom Poms',
        desc: 'The premium choice for luxury hat and fashion accessories. Ultra-soft synthetic fur in 200+ colors — identical look to real fur, 100% cruelty-free.',
        tags: ['3 cm – 20 cm', '200+ Colors', 'Cruelty-Free', 'Best Seller'],
    },
    {
        emoji: '🧶',
        name: 'Wool Pom Poms',
        desc: 'Classic handspun wool pom poms with a rich, textured finish. Perfect for beanies, scarves, key chains, and home décor. Warm and naturally hypoallergenic.',
        tags: ['4 cm – 18 cm', 'Natural Fiber', 'Hypoallergenic', 'Winter Range'],
    },
    {
        emoji: '✨',
        name: 'Metallic Pom Poms',
        desc: 'Eye-catching gold, silver, rose gold, and bronze pom poms for festive decorations, party accessories, and premium fashion collections.',
        tags: ['5 cm – 15 cm', 'Gold / Silver', 'Rose Gold', 'Festive'],
    },
    {
        emoji: '🎀',
        name: 'Giant Pom Poms',
        desc: 'Oversized statement pom poms (15 cm – 25 cm) for runway fashion, photo props, window displays, and bold lifestyle accessories.',
        tags: ['15 cm – 25 cm', 'Statement Piece', 'Display', 'Custom Size'],
    },
    {
        emoji: '🪡',
        name: 'Mini Pom Poms',
        desc: 'Tiny precision pom poms (1 cm – 3 cm) for delicate garment trims, earrings, keychains, stationery, and craft projects.',
        tags: ['1 cm – 3 cm', 'Earrings', 'Trim', 'Craft Projects'],
    },
    {
        emoji: '🧢',
        name: 'Hat & Beanie Pom Poms',
        desc: 'Specifically designed with reinforced stem loops and snap-button detachable options for easy attachment to knitwear, bobble hats, and winter accessories.',
        tags: ['Detachable Option', 'Snap Button', 'Hat Ready', 'Knitwear'],
    },
];

const POM_SUBCATEGORIES = [
    {
        emoji: '🌺',
        name: 'Faux Fur Pom Poms',
        tagline: 'Ultra-fluffy synthetic fur — the premium choice for fashion hats and luxury accessories.',
        tags: ['3 cm – 20 cm', '200+ Colors', 'Cruelty-Free'],
        href: '/products?category=Pom+Pom+Balls&page=1',
    },
    {
        emoji: '🧶',
        name: 'Wool Pom Poms',
        tagline: 'Classic handspun wool with a rich, textured finish — perfect for beanies and scarves.',
        tags: ['4 cm – 18 cm', 'Natural Fiber', 'Winter Range'],
        href: '/products?category=Pom+Pom+Balls&page=1',
    },
    {
        emoji: '✨',
        name: 'Metallic Pom Poms',
        tagline: 'Gold, silver, rose gold, and bronze finishes for festive and premium fashion collections.',
        tags: ['5 cm – 15 cm', 'Gold / Silver', 'Festive'],
        href: '/products?category=Pom+Pom+Balls&page=1',
    },
    {
        emoji: '🎀',
        name: 'Giant Pom Poms',
        tagline: 'Oversized statement pieces (15 cm – 25 cm) for runway fashion and bold displays.',
        tags: ['15 cm – 25 cm', 'Statement Piece', 'Custom Size'],
        href: '/products?category=Pom+Pom+Balls&page=1',
    },
    {
        emoji: '🪡',
        name: 'Mini Pom Poms',
        tagline: 'Tiny precision pom poms for delicate garment trims, earrings, and craft projects.',
        tags: ['1 cm – 3 cm', 'Earrings', 'Trim'],
        href: '/products?category=Pom+Pom+Balls&page=1',
    },
    {
        emoji: '🧢',
        name: 'Hat & Beanie Pom Poms',
        tagline: 'Reinforced stem loops and snap-button detachable options for knitwear and bobble hats.',
        tags: ['Detachable Option', 'Snap Button', 'Knitwear'],
        href: '/products?category=Pom+Pom+Balls&page=1',
    },
];

const SPECS = [
    { icon: '📏', label: 'Available Sizes', value: '1 cm – 25 cm (custom sizes on request)' },
    { icon: '🎨', label: 'Color Range', value: '200+ standard colors + Pantone custom matching' },
    { icon: '🧵', label: 'Materials', value: 'Faux fur, wool, acrylic, metallic, cotton, velvet' },
    { icon: '📦', label: 'Wholesale MOQ', value: 'From 50 pieces per color / style' },
    { icon: '🚢', label: 'Global Shipping', value: 'DHL, FedEx, Airmail — 50+ countries' },
    { icon: '⏱️', label: 'Lead Time', value: '7–21 business days depending on quantity' },
];

const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'What are handmade pom poms?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Handmade pom poms are spherical decorative balls crafted by looping and tying yarn, faux fur, or other fibres around a form and then trimming them into a perfectly round shape. Each pom pom at Arte Craft India is hand-tied and hand-trimmed by skilled artisans in New Delhi, ensuring consistent shape, density, and quality in every piece.',
            },
        },
        {
            '@type': 'Question',
            name: 'What types of pom poms does Arte Craft India make?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Arte Craft India manufactures faux fur pom poms, wool pom poms, metallic pom poms, giant oversized pom poms, mini pom poms, and hat-specific pom poms with detachable snap-button stems. All types are available in custom colors and sizes.',
            },
        },
        {
            '@type': 'Question',
            name: 'What sizes of pom poms are available?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Our pom poms range from 1 cm mini pom poms up to 25 cm giant pom poms. Standard sizes include 3 cm, 5 cm, 7 cm, 8 cm, 10 cm, 12 cm, 15 cm, 18 cm, and 20 cm. Custom sizes are produced on request for any wholesale order.',
            },
        },
        {
            '@type': 'Question',
            name: 'What materials are used to make your pom poms?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'We use high-quality synthetic faux fur (acrylic / polyester), natural wool, metallic lurex yarn, velvet, and cotton yarn depending on the product type. All synthetic materials are hypoallergenic. Our faux fur is 100% cruelty-free and animal-free.',
            },
        },
        {
            '@type': 'Question',
            name: 'Are your pom poms cruelty-free?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — 100% cruelty-free. All faux fur pom poms are made from premium synthetic fibres with no animal fur whatsoever. Our wool pom poms use ethically sourced, mulesing-free wool certified by our suppliers.',
            },
        },
        {
            '@type': 'Question',
            name: 'Can I order pom poms in custom colors?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. We offer 200+ standard colors and can also Pantone-match any custom color for wholesale orders. Simply provide your Pantone code, fabric swatch, or reference image and our team will produce a dye-match sample for approval before bulk production.',
            },
        },
        {
            '@type': 'Question',
            name: 'What is the minimum order quantity (MOQ) for wholesale pom poms?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'The wholesale minimum order quantity is 50 pieces per color and style for standard faux fur and wool pom poms. For custom Pantone colors or non-standard sizes, the MOQ is 100 pieces. Single pieces and small retail quantities are also available with no minimum.',
            },
        },
        {
            '@type': 'Question',
            name: 'How long does production take for a pom pom order?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Standard in-stock pom poms ship within 2–5 business days. Custom color or size orders take 7–14 business days for small quantities (up to 500 pieces) and 3–5 weeks for large wholesale orders (1,000+ pieces). Rush production is available for an additional fee.',
            },
        },
        {
            '@type': 'Question',
            name: 'What are pom poms used for?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Pom poms are used for: hat and beanie toppers (bobble hats), scarf and glove accents, key chains and bag charms, earrings and jewelry, garment trim and fringe borders, festival and party decorations, gift wrapping accents, home décor (cushions, wreaths, wall hangings), craft and DIY projects, and children\'s toys and accessories.',
            },
        },
        {
            '@type': 'Question',
            name: 'Do you ship pom poms internationally?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — we ship to 50+ countries worldwide via DHL Express, FedEx International, and Registered Airmail. All shipments include a tracking number. We regularly supply buyers in the USA, UK, Canada, Australia, Germany, France, UAE, and across Europe and Southeast Asia.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I attach a pom pom to a hat or beanie?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'The most common method is threading the inner cord or stem through the top of the hat and tying it securely on the inside. For easy removal and washing, choose our detachable pom poms with a snap-button base — simply press to attach and pop off before washing. You can also sew the pom pom stem directly to the hat lining with a needle and thread for a permanent fit.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I care for faux fur pom poms?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'For faux fur pom poms: (1) Hand wash gently in cold water with mild detergent. (2) Do not machine wash, tumble dry, or iron directly. (3) Gently squeeze out excess water, reshape into a ball, and air dry. (4) Once dry, fluff with a soft bristle brush or pet slicker brush to restore fullness. (5) Store loosely — do not compress for long periods as this flattens the fibres.',
            },
        },
        {
            '@type': 'Question',
            name: 'What is the difference between faux fur and wool pom poms?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Faux fur pom poms have long, silky fibres that create an ultra-fluffy, glamorous look — ideal for fashion accessories and premium winter hats. They are 100% synthetic and cruelty-free. Wool pom poms have shorter, denser fibres with a matte, textured appearance — they feel softer and warmer, and are better suited for classic knitwear, children\'s items, and a more traditional handcrafted look. Both are handmade at Arte Craft India.',
            },
        },
    ],
};

const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Handmade Pom Poms — Faux Fur & Wool',
    description:
        'Premium handmade pom poms manufactured in New Delhi, India. Available in faux fur, wool, metallic, mini, and giant sizes. 200+ colors, custom orders, wholesale MOQ from 50 pieces. Ships to 50+ countries.',
    brand: { '@type': 'Brand', name: 'Arte Craft India' },
    manufacturer: {
        '@type': 'Organization',
        name: 'Arte Craft India',
        url: 'https://www.artecraftindia.com',
        telephone: '+91-9818548369',
        email: 'info@artecraftindia.com',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '280/13 Gali No. 29 Molarband Extn.',
            addressLocality: 'Badarpur, New Delhi',
            addressRegion: 'Delhi',
            postalCode: '110044',
            addressCountry: 'IN',
        },
        foundingDate: '2017',
    },
    offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'Arte Craft India' },
    },
    material: 'Faux Fur, Wool, Acrylic Yarn, Metallic Yarn',
    color: '200+ colors available',
    countryOfOrigin: 'India',
};

const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.artecraftindia.com' },
        { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://www.artecraftindia.com/products' },
        { '@type': 'ListItem', position: 3, name: 'Pom Poms', item: 'https://www.artecraftindia.com/products/pom-poms' },
    ],
};

const PomPomsPage: React.FC = () => {
    return (
        <div className="pp-page">

            <SeoHead {...PomPomsSeo} />

            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(productStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbStructuredData)}</script>
            </Helmet>

            {/* ── BREADCRUMB ── */}
            <nav aria-label="Breadcrumb" className="pp-breadcrumb-nav">
                <ol
                    className="pp-breadcrumb"
                    itemScope
                    itemType="https://schema.org/BreadcrumbList"
                >
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/" itemProp="item"><span itemProp="name">Home</span></a>
                        <meta itemProp="position" content="1" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/products" itemProp="item"><span itemProp="name">Products</span></a>
                        <meta itemProp="position" content="2" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span itemProp="name" aria-current="page">Pom Poms</span>
                        <meta itemProp="position" content="3" />
                    </li>
                </ol>
            </nav>

            {/* ── HERO ── */}
            <section className="pp-hero" aria-labelledby="pp-hero-h1">
                <div className="pp-hero-dots" aria-hidden="true">
                    {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                </div>
                <div className="pp-hero-inner">
                    <div className="pp-hero-badge" aria-hidden="true">
                        Manufacturer Direct · Est. 2017 · New Delhi, India
                    </div>
                    <h1 id="pp-hero-h1">
                        Handmade{' '}
                        <span className="pp-hero-accent">Pom Poms</span>
                        <br />
                        Premium Quality · Custom Colors · Wholesale
                    </h1>
                    <p className="pp-hero-desc speakable-summary">
                        Direct-from-manufacturer handmade pom poms in faux fur, wool, metallic &amp; more.
                        200+ colors, sizes 1 cm – 25 cm, cruelty-free. Custom orders and bulk wholesale
                        supplied to 50+ countries since 2017.
                    </p>
                    <div className="pp-hero-stats" role="list" aria-label="Key product highlights">
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">200+</span>
                            <span className="pp-stat-label">Colors</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">1–25cm</span>
                            <span className="pp-stat-label">Sizes</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">50+</span>
                            <span className="pp-stat-label">Countries</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">100%</span>
                            <span className="pp-stat-label">Cruelty-Free</span>
                        </div>
                    </div>
                    <div className="pp-hero-cta">
                        <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                            Request a Wholesale Quote
                        </a>
                        <a href="/contact" className="pp-btn pp-btn-outline">
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            {/* ── TRUST STRIP ── */}
            <div className="pp-trust-strip" aria-label="Why buy from Arte Craft India">
                <div className="pp-trust-inner">
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🤲</div>
                        <div className="pp-trust-text">
                            <strong>100% Handmade</strong>
                            <span>Every pom pom hand-crafted</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🎨</div>
                        <div className="pp-trust-text">
                            <strong>Custom Colors</strong>
                            <span>Pantone matching available</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">📦</div>
                        <div className="pp-trust-text">
                            <strong>Wholesale MOQ 50 pcs</strong>
                            <span>Tiered volume pricing</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🌍</div>
                        <div className="pp-trust-text">
                            <strong>Ships Worldwide</strong>
                            <span>DHL, FedEx, Airmail</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🐇</div>
                        <div className="pp-trust-text">
                            <strong>Cruelty-Free</strong>
                            <span>100% animal-free faux fur</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── PRODUCT TYPES ── */}
            <main id="main-content" role="main">
                <div className="pp-wrapper">

                    <div className="pp-section-header">
                        <span className="pp-section-label">Our Range</span>
                        <h2>Pom Pom Types & Collections</h2>
                        <p className="pp-section-sub">
                            Six distinct pom pom styles — each crafted for a specific use case,
                            all available in custom colors and wholesale quantities.
                        </p>
                    </div>

                    <div className="pp-types-grid">
                        {PRODUCT_TYPES.map(pt => (
                            <article key={pt.name} className="pp-type-card">
                                <span className="pp-type-emoji" aria-hidden="true">{pt.emoji}</span>
                                <h3>{pt.name}</h3>
                                <p>{pt.desc}</p>
                                <div className="pp-type-tags" aria-label={`${pt.name} specifications`}>
                                    {pt.tags.map(t => <span key={t} className="pp-tag">{t}</span>)}
                                </div>
                            </article>
                        ))}
                    </div>

                    {/* ── BROWSE COLLECTIONS ── */}
                    <section className="pp-browse-section" aria-labelledby="pp-browse-h2">
                        <div className="pp-section-header">
                            <span className="pp-section-label">Shop by Type</span>
                            <h2 id="pp-browse-h2">Browse Pom Pom Collections</h2>
                            <p className="pp-section-sub">
                                Explore each pom pom type in detail — sizes, colors, materials, and ordering options.
                            </p>
                        </div>
                        <div className="pp-browse-grid">
                            {POM_SUBCATEGORIES.map(sub => (
                                <a key={sub.name} href={sub.href} className="pp-browse-card">
                                    <span className="pp-browse-emoji" aria-hidden="true">{sub.emoji}</span>
                                    <h3 className="pp-browse-name">{sub.name}</h3>
                                    <p className="pp-browse-tagline">{sub.tagline}</p>
                                    <div className="pp-browse-tags" aria-label={`${sub.name} highlights`}>
                                        {sub.tags.map(t => <span key={t} className="pp-tag">{t}</span>)}
                                    </div>
                                    <span className="pp-browse-cta">View Collection →</span>
                                </a>
                            ))}
                        </div>
                    </section>

                    {/* ── ABOUT / SPECIFICATIONS ── */}
                    <section className="pp-about-grid" aria-labelledby="pp-about-h2">
                        <div className="pp-about-prose">
                            <h2 id="pp-about-h2">
                                India's Premier Handmade Pom Pom Manufacturer
                            </h2>
                            <p>
                                <strong>Arte Craft India</strong> has been producing handmade pom poms from New Delhi since
                                2017. We are a <strong>direct manufacturer</strong> — not a middleman — which means every
                                pom pom you receive is made in our own workshop by our own skilled artisans, giving you
                                the best possible quality at factory-direct prices.
                            </p>
                            <p>
                                Our pom poms are trusted by fashion brands, knitwear manufacturers, accessories designers,
                                and craft retailers across the <strong>USA, UK, Canada, Australia, Europe, and the Middle
                                East</strong>. We handle everything from single-piece retail orders to large-volume
                                wholesale contracts of 10,000+ units.
                            </p>
                            <p>
                                Every pom pom leaves our factory inspected for <strong>shape consistency, colour accuracy,
                                and stem security</strong>. We use premium-grade faux fur fibres and natural wool to ensure
                                our products maintain their full, round appearance wash after wash.
                            </p>
                        </div>
                        <div className="pp-specs-list" aria-label="Product specifications">
                            {SPECS.map(s => (
                                <div key={s.label} className="pp-spec-row">
                                    <div className="pp-spec-icon" aria-hidden="true">{s.icon}</div>
                                    <div className="pp-spec-text">
                                        <strong>{s.label}</strong>
                                        <span>{s.value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── FAQ ── */}
                    <section aria-labelledby="pp-faq-h2" itemScope itemType="https://schema.org/FAQPage">
                        <div className="pp-section-header" style={{ paddingTop: 0 }}>
                            <span className="pp-section-label">Knowledge Base</span>
                            <h2 id="pp-faq-h2">Frequently Asked Questions — Pom Poms</h2>
                            <p className="pp-section-sub">
                                Everything you need to know before placing your order — from materials and
                                care to wholesale pricing and international shipping.
                            </p>
                        </div>

                        {/* ── About Pom Poms ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            About Handmade Pom Poms
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What are handmade pom poms?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Definition</span>
                                        <p itemProp="text">
                                            <strong>Handmade pom poms</strong> are spherical decorative balls crafted by looping and tying
                                            yarn, faux fur, or other fibres around a form and then trimming them into a perfectly round
                                            shape. Each pom pom at Arte Craft India is hand-tied and hand-trimmed by skilled artisans
                                            in New Delhi, ensuring consistent shape, density, and quality in every piece.
                                        </p>
                                        <p>
                                            Unlike machine-made pom poms, handmade pom poms have a <strong>denser, rounder</strong> profile
                                            and a more even fibre distribution. They are used as decorative toppers for hats and beanies,
                                            garment trims, bag charms, key chains, jewelry, gift wrapping, home décor, and festival decorations.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What types of pom poms does Arte Craft India make?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Product Range</span>
                                        <p itemProp="text">We manufacture six main categories of pom poms:</p>
                                        <ul>
                                            <li><strong>Faux Fur Pom Poms</strong> — Ultra-fluffy synthetic fur, 200+ colors, 3–20 cm</li>
                                            <li><strong>Wool Pom Poms</strong> — Classic handspun, natural feel, 4–18 cm</li>
                                            <li><strong>Metallic Pom Poms</strong> — Gold, silver, rose gold, bronze finishes</li>
                                            <li><strong>Giant Pom Poms</strong> — 15–25 cm statement pieces for fashion and display</li>
                                            <li><strong>Mini Pom Poms</strong> — 1–3 cm for trim, earrings, and craft projects</li>
                                            <li><strong>Hat Pom Poms</strong> — With reinforced stems and detachable snap-button options</li>
                                        </ul>
                                        <p>All types are available with custom color matching, custom sizing, and custom packaging for wholesale buyers.</p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What sizes of pom poms are available?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Sizes</span>
                                        <p itemProp="text">
                                            Our pom poms range from <strong>1 cm mini pom poms</strong> up to <strong>25 cm giant pom poms</strong>.
                                            Standard stocked sizes are: <strong>3 cm, 5 cm, 7 cm, 8 cm, 10 cm, 12 cm, 15 cm, 18 cm,</strong> and
                                            <strong> 20 cm</strong>. Custom sizes between these increments are available for wholesale orders.
                                        </p>
                                        <p>
                                            For hat pom poms, the most popular sizes for adult beanies are <strong>8–10 cm</strong>; for children's
                                            hats, <strong>5–7 cm</strong> is standard. Please contact us if your size requirement is outside the
                                            standard range.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What materials are used to make your pom poms?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Materials</span>
                                        <p itemProp="text">We use carefully selected materials for each pom pom type:</p>
                                        <ul>
                                            <li><strong>Faux Fur</strong> — Premium polyester/acrylic synthetic fur; hypoallergenic, cruelty-free, washable</li>
                                            <li><strong>Wool Yarn</strong> — Ethically sourced, mulesing-free natural wool for a traditional feel</li>
                                            <li><strong>Acrylic Yarn</strong> — Budget-friendly, vibrant color retention, lightweight</li>
                                            <li><strong>Metallic Lurex</strong> — Woven metallic filament for gold, silver, and rose gold effects</li>
                                            <li><strong>Velvet Yarn</strong> — Soft velveteen texture for premium decorative applications</li>
                                            <li><strong>Cotton Yarn</strong> — Natural, breathable, ideal for baby and sensitive-skin applications</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Are your pom poms cruelty-free and vegan?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Ethics &amp; Sustainability</span>
                                        <p itemProp="text">
                                            <strong>Yes — all our faux fur pom poms are 100% cruelty-free and vegan.</strong> They are made
                                            entirely from synthetic acrylic and polyester fibres with zero animal content. There is no real fur,
                                            no down, and no animal-derived materials of any kind in our faux fur range.
                                        </p>
                                        <p>
                                            Our wool pom poms use ethically sourced natural wool from suppliers certified mulesing-free.
                                            If strict vegan certification is required for your brand, we recommend the faux fur or acrylic
                                            yarn range and can provide material composition documentation on request.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Use Cases ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Uses &amp; Applications
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What are pom poms used for?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Applications</span>
                                        <p itemProp="text">Pom poms are among the most versatile fashion and craft accessories. Common uses include:</p>
                                        <ul>
                                            <li><strong>Hat toppers</strong> — the classic bobble on beanies, ski hats, and children's winter hats</li>
                                            <li><strong>Bag charms &amp; key chains</strong> — oversized faux fur pom poms as luxury accessories</li>
                                            <li><strong>Garment trim</strong> — rows of mini pom poms along hems, cuffs, and necklines</li>
                                            <li><strong>Earrings &amp; jewelry</strong> — lightweight mini pom poms as statement jewelry</li>
                                            <li><strong>Scarf &amp; glove accents</strong> — pom pom tails on scarves and fingers of gloves</li>
                                            <li><strong>Home décor</strong> — pom pom garlands, wreaths, cushion trims, wall hangings</li>
                                            <li><strong>Gift wrapping</strong> — as decorative toppers on gift boxes and bags</li>
                                            <li><strong>Festival &amp; event décor</strong> — party backdrops, balloon alternatives, table centerpieces</li>
                                            <li><strong>DIY craft projects</strong> — school projects, handmade toys, greeting cards</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How do I attach a pom pom to a hat or beanie?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Attachment Guide</span>
                                        <p itemProp="text">There are three common attachment methods:</p>
                                        <ol>
                                            <li><strong>Stem-through method (permanent)</strong> — Thread the inner cord through the top of the knitted hat and knot it securely on the inside. This is the most durable method for machine-knit hats.</li>
                                            <li><strong>Snap-button (detachable)</strong> — Choose our detachable pom poms with an integrated snap-button base. Simply press to attach and pop off before washing the hat. Ideal for easy care and interchangeable styles.</li>
                                            <li><strong>Sew-on (for any hat)</strong> — Use a yarn needle and matching thread to sew the pom pom stem or base onto the hat crown. Works on any hat type including crochet, woven, and fleece.</li>
                                        </ol>
                                        <p>We recommend the <strong>snap-button detachable option</strong> for retail knitwear brands — it lets end consumers wash their hats without damaging the pom pom.</p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What is the difference between faux fur and wool pom poms?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Comparison</span>
                                        <p itemProp="text">
                                            <strong>Faux fur pom poms</strong> have long, silky fibres (3–5 cm pile length) that create an
                                            ultra-fluffy, glamorous, and tactile look. They are 100% synthetic, cruelty-free, hold their shape
                                            well in humidity, and are the preferred choice for premium fashion accessories and winter hat brands.
                                        </p>
                                        <p>
                                            <strong>Wool pom poms</strong> have shorter, denser fibres with a matte, textured, naturally warm
                                            appearance. They feel softer against skin and are better suited for traditional knitwear, children's
                                            hats, and rustic craft aesthetics. Natural wool is biodegradable and breathable.
                                        </p>
                                        <p>
                                            For a <strong>luxury statement look</strong> — choose faux fur. For a <strong>classic handcrafted feel</strong> — choose wool.
                                            Both are handmade to the same quality standards at our New Delhi workshop.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Wholesale & Ordering ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Wholesale &amp; Custom Orders
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What is the minimum order quantity (MOQ) for wholesale pom poms?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">MOQ &amp; Pricing</span>
                                        <p itemProp="text">
                                            For <strong>in-stock standard colors</strong>, there is no minimum — you can order a single pom pom.
                                            For <strong>custom colors or non-standard sizes</strong>, the MOQ is <strong>50 pieces per style</strong>.
                                            For <strong>Pantone-matched custom dye lots</strong>, the MOQ is <strong>100 pieces per color</strong>.
                                        </p>
                                        <p>
                                            Wholesale volume price breaks are offered at <strong>100, 250, 500, 1,000, and 5,000+ units</strong>.
                                            Contact us for a full pricing sheet with tiered wholesale rates. Private label and custom packaging
                                            are available for orders of 500+ pieces.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Can I order pom poms in custom colors?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Custom Colors</span>
                                        <p itemProp="text">
                                            <strong>Yes — custom color pom poms are one of our most popular services.</strong> We offer
                                            200+ standard colors and full Pantone color matching for wholesale orders. To order:
                                        </p>
                                        <ol>
                                            <li>Share your <strong>Pantone code, fabric swatch, or reference image</strong></li>
                                            <li>We produce a <strong>dye-match sample</strong> within 5–7 business days</li>
                                            <li>You approve the sample and confirm the order</li>
                                            <li>Bulk production begins after your written approval</li>
                                        </ol>
                                        <p>
                                            Custom color pom poms are ideal for fashion brands that need their accessories to match a specific
                                            season palette or garment colorway exactly.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How long does production take for a pom pom order?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Lead Times</span>
                                        <p itemProp="text">Production timelines vary by order size and type:</p>
                                        <ul>
                                            <li><strong>In-stock standard pom poms:</strong> Ships in 2–5 business days</li>
                                            <li><strong>Custom color / size (up to 200 pcs):</strong> 7–14 business days</li>
                                            <li><strong>Medium wholesale (200–1,000 pcs):</strong> 3–4 weeks</li>
                                            <li><strong>Large wholesale (1,000–10,000 pcs):</strong> 4–8 weeks</li>
                                            <li><strong>Rush production:</strong> Available for small orders — contact us with your deadline</li>
                                        </ul>
                                        <p>All lead times begin <strong>after sample approval</strong>, not from the inquiry date. We can accommodate seasonal rush deadlines — please enquire at least 4 weeks in advance for large holiday-season orders.</p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Care & Shipping ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Care, Shipping &amp; After-Sales
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How do I care for faux fur pom poms?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Care Instructions</span>
                                        <p itemProp="text">Follow these steps to keep your faux fur pom poms looking full and beautiful for years:</p>
                                        <ol>
                                            <li><strong>Hand wash only</strong> — gently in cold water (max 30°C) with a mild detergent</li>
                                            <li><strong>Do not machine wash</strong>, tumble dry, or iron directly on the fur</li>
                                            <li>Gently squeeze out excess water — do not wring or twist</li>
                                            <li><strong>Air dry</strong> in a well-ventilated area; reshape into a ball while damp</li>
                                            <li>Once fully dry, <strong>fluff with a soft bristle brush</strong> or pet slicker brush to restore the full round shape</li>
                                            <li>Store loosely — <strong>do not compress</strong> for long periods as sustained pressure flattens the pile</li>
                                        </ol>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Do you ship pom poms internationally?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">International Shipping</span>
                                        <p itemProp="text">
                                            <strong>Yes — we ship handmade pom poms to 50+ countries worldwide.</strong> All orders include a
                                            tracking number. Available shipping options:
                                        </p>
                                        <ul>
                                            <li><strong>DHL Express</strong> — fastest, typically 3–5 business days internationally</li>
                                            <li><strong>FedEx International</strong> — reliable, 4–7 business days</li>
                                            <li><strong>Registered Airmail</strong> — economy option, 10–21 business days</li>
                                        </ul>
                                        <p>
                                            We regularly supply buyers in the <strong>USA, UK, Canada, Australia, Germany, France, Netherlands,
                                            UAE, Saudi Arabia, Japan, South Korea</strong>, and across Europe and South-East Asia.
                                            Import duties and taxes are the responsibility of the buyer.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Can pom poms have a detachable snap button for easy hat washing?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Detachable Option</span>
                                        <p itemProp="text">
                                            <strong>Yes — our detachable snap-button pom poms are a popular choice for knitwear brands.</strong> The
                                            pom pom base has a sewn-in female snap; you sew the corresponding male snap onto the inside of the
                                            hat crown. The pom pom clicks on and pops off in seconds.
                                        </p>
                                        <p>
                                            This allows end consumers to remove the pom pom before machine-washing the hat, dramatically extending
                                            the life of both the hat and the pom pom. Detachable snap pom poms are also interchangeable — one hat
                                            can be paired with multiple pom pom colors. Available in all materials and sizes from 5 cm upward.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>
                    </section>

                    {/* ── CTA ── */}
                    <section className="pp-cta-banner" aria-labelledby="pp-cta-h2">
                        <h2 id="pp-cta-h2">Ready to Order Handmade Pom Poms?</h2>
                        <p>
                            Get a free wholesale quote within 24 hours. Custom colors, bulk orders,
                            and private label packaging available. Trusted by brands in 50+ countries.
                        </p>
                        <div className="pp-cta-buttons">
                            <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                                Request a Wholesale Quote
                            </a>
                            <a href="/contact" className="pp-btn pp-btn-outline">
                                Ask a Question
                            </a>
                        </div>
                    </section>

                </div>
            </main>
        </div>
    );
};

export default PomPomsPage;
